import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, Chip, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Footer from '../components/landing/Footer';
import { trackButtonClick } from '../utils/gtm';
import { PAGE_CONTAINER_SX } from '../components/landing/pageLayout';

const BLOG_POSTS = [
  {
    id: 1,
    title: "How AI Code Review Catches 80% of Defects Before QA",
    excerpt: "Traditional code review misses critical defects that slip into production. Discover how Sniffr.ai's AI-powered review engine identifies bugs, security vulnerabilities, and requirement mismatches before they reach your QA team.",
    category: "Engineering",
    date: "March 2026",
    readTime: "5 min read",
    image: "/assets/logo.png",
  },
  {
    id: 2,
    title: "From Manual UAT to AI Automation: A Developer's Guide",
    excerpt: "Manual User Acceptance Testing is slow, expensive, and error-prone. Learn how engineering teams are replacing manual UAT cycles with AI-driven validation that runs on every pull request.",
    category: "UAT Automation",
    date: "March 2026",
    readTime: "7 min read",
    image: "/assets/logo.png",
  },
  {
    id: 3,
    title: "Integrating Sniffr.ai with GitHub, GitLab & Azure DevOps",
    excerpt: "A step-by-step guide to connecting Sniffr.ai with your existing CI/CD pipeline. Get AI-powered PR analysis running in under 10 minutes on any major Git platform.",
    category: "Integrations",
    date: "February 2026",
    readTime: "6 min read",
    image: "/assets/logo.png",
  },
  {
    id: 4,
    title: "Requirement Validation with AI: Closing the Gap Between Specs and Code",
    excerpt: "One of the biggest causes of production bugs is the disconnect between written requirements and implemented code. See how Sniffr.ai validates acceptance criteria against every pull request automatically.",
    category: "Product",
    date: "February 2026",
    readTime: "8 min read",
    image: "/assets/logo.png",
  },
  {
    id: 5,
    title: "Engineering KPIs That Actually Matter: Measuring Code Quality with AI",
    excerpt: "Velocity metrics only tell part of the story. Explore the engineering health metrics that CTOs and VPs of Engineering should be tracking — and how AI surfaces them automatically.",
    category: "Leadership",
    date: "January 2026",
    readTime: "6 min read",
    image: "/assets/logo.png",
  },
  {
    id: 6,
    title: "Security Code Review at Scale: How AI Fills the Gap",
    excerpt: "Security vulnerabilities introduced during development are costly to fix post-production. Learn how AI-powered security code review helps teams catch OWASP Top 10 issues on every PR, not just during audits.",
    category: "Security",
    date: "January 2026",
    readTime: "5 min read",
    image: "/assets/logo.png",
  },
];

const CATEGORY_COLORS = {
  Engineering: "#3b82f6",
  "UAT Automation": "#8b5cf6",
  Integrations: "#10b981",
  Product: "#f59e0b",
  Leadership: "#ef4444",
  Security: "#06b6d4",
};

export default function Blog() {
  const navigate = useNavigate();

  const handleBookDemo = () => {
    trackButtonClick('book_sniffr_demo', 'blog_page');
    navigate('/contact');
  };

  return (
    <>
      <SEO
        title="Blog | Sniffr.ai - AI Code Review & UAT Automation Insights"
        description="Explore engineering insights, AI code review best practices, UAT automation guides, and product updates from the Sniffr.ai team. Stay ahead with the latest in AI-driven software quality."
        keywords="AI code review blog, UAT automation insights, engineering blog, pull request review, software quality, Sniffr.ai blog"
        url="/blog"
      />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
        <Box component="main" sx={{ flex: 1 }}>
          {/* Hero */}
          <Box sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', py: { xs: 6, md: 10 } }}>
            <Container maxWidth="xl" sx={PAGE_CONTAINER_SX}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '32px', md: '48px' },
                    fontWeight: 700,
                    color: '#141414',
                    mb: 2,
                    fontFamily: 'MD IO, monospace',
                  }}
                >
                  Sniffr.ai Blog
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '16px', md: '20px' },
                    fontWeight: 400,
                    color: '#616161',
                    maxWidth: '640px',
                    mx: 'auto',
                    fontFamily: 'Segoe UI, monospace',
                    lineHeight: 1.6,
                  }}
                >
                  Engineering insights, AI code review best practices, and product updates from the Sniffr.ai team.
                </Typography>
              </Box>
            </Container>
          </Box>

          {/* Blog Posts Grid */}
          <Container maxWidth="xl" sx={{ ...PAGE_CONTAINER_SX, py: { xs: 6, md: 10 } }}>
            <Grid container spacing={4}>
              {BLOG_POSTS.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        transform: 'translateY(-2px)',
                        borderColor: '#d1d5db',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        background: `linear-gradient(135deg, ${CATEGORY_COLORS[post.category] || '#3b82f6'}22 0%, ${CATEGORY_COLORS[post.category] || '#3b82f6'}44 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px 8px 0 0',
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/logo.png"
                        alt="Sniffr.ai"
                        sx={{ height: 48, width: 48, borderRadius: '50%', opacity: 0.8 }}
                      />
                    </Box>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            bgcolor: `${CATEGORY_COLORS[post.category] || '#3b82f6'}18`,
                            color: CATEGORY_COLORS[post.category] || '#3b82f6',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            fontFamily: 'Segoe UI, monospace',
                          }}
                        />
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'Segoe UI, monospace' }}>
                          {post.readTime}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#141414',
                          mb: 1.5,
                          lineHeight: 1.4,
                          fontFamily: 'Segoe UI, monospace',
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          flex: 1,
                          fontFamily: 'Segoe UI, monospace',
                        }}
                      >
                        {post.excerpt}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#9ca3af',
                          fontSize: '0.75rem',
                          mt: 2,
                          fontFamily: 'Segoe UI, monospace',
                        }}
                      >
                        {post.date}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* CTA Banner */}
            <Box
              sx={{
                mt: { xs: 8, md: 12 },
                p: { xs: 4, md: 8 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #141414 0%, #374151 100%)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: { xs: '24px', md: '32px' },
                  mb: 2,
                  fontFamily: 'MD IO, monospace',
                }}
              >
                Ready to catch defects before QA?
              </Typography>
              <Typography
                sx={{
                  color: '#d1d5db',
                  fontSize: { xs: '16px', md: '18px' },
                  mb: 4,
                  maxWidth: '560px',
                  mx: 'auto',
                  fontFamily: 'Segoe UI, monospace',
                }}
              >
                Join engineering teams using Sniffr.ai to ship faster with AI-powered code review and requirement validation.
              </Typography>
              <Button
                variant="contained"
                onClick={handleBookDemo}
                sx={{
                  background: 'linear-gradient(135deg, #FF8052 0%, #B366FF 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 5,
                  py: 1.5,
                  borderRadius: 1,
                  textTransform: 'none',
                  boxShadow: 'none',
                  fontFamily: 'Segoe UI, monospace',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e66d3f 0%, #9952e0 100%)',
                    boxShadow: '0 4px 16px rgba(179, 102, 255, 0.35)',
                  },
                }}
              >
                Book Sniffr Demo
              </Button>
            </Box>
          </Container>
        </Box>
        <Footer />
      </Box>
    </>
  );
}
